import { htmlSafe } from '@ember/template';
import { not } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import AppContentCard from 'tio-common/components/app/content-card';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import inc from 'tio-common/helpers/inc';
import { PhPrinter } from 'ember-phosphor-icons';
import screenIs from 'tio-common/helpers/screen-is';
import Table from 'tio-ui/components/table';
import DownloadButton from 'tio-ui/components/download-button';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import ENV from 'tio-employee/config/environment';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';

export interface ForgivenessEstimatorResultInstructionsSignature {
  Args: {
    recommendation: RecommendationModel;
  };
}

export default class ForgivenessEstimatorResultInstructionsComponent extends Component<ForgivenessEstimatorResultInstructionsSignature> {
  // Provides request auth headers to DownloadButton; keeps that UI component free of any store/adapter tie.
  // WarpDrive Phase 2 — replaces adapter.headers read; auth-context is owner-free (ADR-001).
  downloadHeaders = (): Record<string, string> =>
    Object.fromEntries(buildTioAuthHeaders({ apiKey: ENV.apiKey }).entries());

  get instructions() {
    return this.args.recommendation?.nextSteps || [];
  }

  downloadAndOpenInNewTab = dropTask(async (url: string) => {
    if (!url) {
      return;
    }
    const headers = buildTioAuthHeaders({ apiKey: ENV.apiKey });
    const response = await fetch(url, { headers });
    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noreferrer noopener');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  <template>
    {{#if (screenIs "md")}}
      <Section>
        <:header>
          <div class="tio-flex-end text-sm gap-x-4">
            <Button
              @appearance="outlined"
              @intent="primary"
              @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
              {{on "click" (fn this.downloadAndOpenInNewTab.perform @recommendation.downloadUrl)}}
            >
              <PhPrinter @size="16" class="mr-2" />
              {{t "common.print"}}
            </Button>
            <DownloadButton
              @authHeaders={{this.downloadHeaders}}
              @url={{@recommendation.downloadUrl}}
              @filename={{@recommendation.downloadFilename}}
            />
          </div>
        </:header>
        <:body>
          <Table>
            <:thead as |options|>
              <options.tr>
                {{#each this.instructions as |column|}}
                  <options.th>
                    {{column.title}}
                    {{#if column.subtitle}}
                      <div class="font-normal">
                        {{column.subtitle}}
                      </div>
                    {{/if}}
                  </options.th>
                {{/each}}
              </options.tr>
            </:thead>
            <:tbody as |options|>
              <options.tr>
                {{#each this.instructions as |row|}}
                  <options.td class="align-top">
                    {{#if row.subtitle}}
                      <p class="font-semibold mx-2 mt-4 min-h-12">{{row.subtitle}}:</p>
                    {{/if}}
                    <ul class="flex-col top-10">
                      {{#each row.instructions as |step index|}}
                        <li class="flex my-4 mx-2">
                          <div class="rounded-full mr-2 w-6 h-6 tio-row-center shrink-0">{{inc
                              index
                            }}</div>
                          <span class="step-by-step-table-links">
                            {{htmlSafe step}}
                          </span>
                        </li>
                      {{/each}}
                    </ul>
                  </options.td>
                {{/each}}
              </options.tr>
            </:tbody>
          </Table>
        </:body>
      </Section>
    {{else}}
      {{#each this.instructions as |card|}}
        <AppContentCard @title={{card.title}} class="m-4 even:tio-bg-info-soft">
          {{#if card.subtitle}}
            <h2 class="italicized">{{card.subtitle}}:</h2>
          {{/if}}
          <ul class="flex-col">
            {{#each card.instructions as |step index|}}
              <li class="flex my-4 mx-2">
                <div class="rounded-full mr-2 w-6 h-6 tio-row-center shrink-0">{{inc index}}</div>
                <span class="step-by-step-table-links">
                  {{htmlSafe step}}
                </span>
              </li>
            {{/each}}
          </ul>
        </AppContentCard>
      {{/each}}

      {{#if (not (screenIs "md"))}}
        <div class="tio-row-flush w-full justify-around">
          <Button
            @appearance="outlined"
            @intent="primary"
            @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
            {{on "click" (fn this.downloadAndOpenInNewTab.perform @recommendation.downloadUrl)}}
            class="border-none"
          >
            <PhPrinter @size="16" class="mr-2" />
            {{t "common.print"}}
          </Button>
          <DownloadButton
            @authHeaders={{this.downloadHeaders}}
            @url={{@recommendation.downloadUrl}}
            @filename={{@recommendation.downloadFilename}}
            class="border-none"
          />
        </div>
      {{/if}}
    {{/if}}
  </template>
}
